import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ChatMessage } from "@/api/types";
import { splitSources } from "@/api/client";
import { cn } from "@/lib/cn";
import { SafeLink } from "@/lib/mdLink";
import SourceList from "./SourceList";
import AnswerActions from "./AnswerActions";
import ConfirmCard from "./ConfirmCard";
import { AlertTriangle, Loader2, Pencil, Download, Info, FileText, Image as ImageIcon } from "lucide-react";

// 첨부 칩용 파일 크기 표기
function fmtAttachSize(bytes: number): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

// 텍스트 첨부 "내용 보기" — 전송 직후엔 preview 즉시 표시, 이력 복원(서버 보관본)은
// 펼치는 시점에 /api/chat/attachment/{token} 에서 내용을 지연 로드한다.
const TEXT_VIEW_LIMIT = 5000;

function TextAttachmentView({ name, preview, url }: { name: string; preview?: string; url?: string }) {
  const [body, setBody] = useState<string | null>(preview ?? null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const onToggle = (e: { currentTarget: HTMLDetailsElement }) => {
    if (!e.currentTarget.open || body !== null || loading || !url) return;
    setLoading(true);
    fetch(url, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) =>
        setBody(t.slice(0, TEXT_VIEW_LIMIT) + (t.length > TEXT_VIEW_LIMIT ? "\n…(이하 생략)" : "")),
      )
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  };

  return (
    <details className="rounded-lg bg-white/15 px-2 py-1 text-xs" onToggle={onToggle}>
      <summary className="cursor-pointer select-none opacity-90">📄 {name} 내용 보기</summary>
      {loading ? (
        <div className="mt-1 flex items-center gap-1.5 p-1 text-[11px] opacity-80">
          <Loader2 className="h-3 w-3 animate-spin" /> 내용 불러오는 중…
        </div>
      ) : failed ? (
        <div className="mt-1 p-1 text-[11px] opacity-80">내용을 불러오지 못했습니다(보존기간 경과 등).</div>
      ) : (
        <pre className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded bg-black/15 p-2 text-[11px] leading-relaxed">
          {body ?? ""}
        </pre>
      )}
    </details>
  );
}

// 기능①: 답변 본문의 [n] 각주를 클릭 가능한 링크(#cite-n)로 변환.
// n 이 실제 참고문서 개수 범위(1..count) 안일 때만 변환(오탐 방지: "[2026]" 등).
function linkifyCitations(body: string, srcCount: number): string {
  if (!srcCount || !body) return body;
  return body.replace(/\[(\d{1,2})\]/g, (full, num) => {
    const n = parseInt(num, 10);
    return n >= 1 && n <= srcCount ? `[[${n}]](#cite-${n})` : full;
  });
}

// 각주 클릭 → 해당 참고문서 행으로 스크롤 + 잠깐 강조.
function scrollToCite(idPrefix: string, n: string): void {
  const el = document.getElementById(`${idPrefix}-${n}`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("cite-flash");
  window.setTimeout(() => el.classList.remove("cite-flash"), 1600);
}

// ── 대기시간 표시(2026-08-06) ─────────────────────────────────────
// "답변 처리중이 오래 걸린다"는 민원 대응: 경과시간을 초 단위로 세고,
// 시간대에 따라 안내 문구를 바꿔 체감 대기시간을 줄인다.
// 스킬 진행률(progress)이 있으면 진행바와 함께 경과시간을 표시한다.
const WAIT_STAGES: { after: number; text: string }[] = [
  { after: 0, text: "답변 작성 중…" },
  { after: 8, text: "관련 문서를 찾아 정리하고 있어요…" },
  { after: 20, text: "찾은 내용을 바탕으로 답변을 작성하고 있어요…" },
  { after: 45, text: "질문이 복잡해 시간이 조금 더 걸리고 있어요…" },
  { after: 90, text: "복잡한 질문은 1~2분 이상 걸릴 수 있어요…" },
];

function formatElapsed(sec: number): string {
  return sec < 60 ? `${sec}초` : `${Math.floor(sec / 60)}분 ${sec % 60}초`;
}

function WaitingIndicator({
  startedAt,
  progress,
}: {
  startedAt?: number;
  progress?: { phase: string; done: number; total: number; message: string } | null;
}) {
  // startedAt 이 없으면(이력 복원 등 예외 케이스) 마운트 시각 기준으로 센다.
  const startRef = useRef<number>(startedAt ?? Date.now());
  const [elapsed, setElapsed] = useState<number>(() =>
    Math.max(0, Math.floor((Date.now() - startRef.current) / 1000)),
  );
  useEffect(() => {
    const t = window.setInterval(() => {
      setElapsed(Math.max(0, Math.floor((Date.now() - startRef.current) / 1000)));
    }, 1000);
    return () => window.clearInterval(t);
  }, []);

  // 진행률 이벤트(보고서 종합 등)가 오면 그 메시지를 우선 사용
  let text = WAIT_STAGES[0].text;
  if (progress) {
    text = progress.message || "보고서 종합 중…";
  } else {
    for (const st of WAIT_STAGES) if (elapsed >= st.after) text = st.text;
  }

  return (
    <div className="flex min-w-[220px] flex-col gap-1.5 text-muted-foreground">
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        <span>{text}</span>
        <span className="ml-auto shrink-0 pl-3 text-xs tabular-nums text-muted-foreground/70">
          {formatElapsed(elapsed)}
        </span>
      </div>
      {progress && progress.total > 0 && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-[#EB4E26] transition-all duration-300"
            style={{
              width: `${Math.min(100, Math.round((progress.done / progress.total) * 100))}%`,
            }}
          />
        </div>
      )}
      {elapsed >= 60 && (
        <div className="text-xs text-muted-foreground/70">
          답변이 완성되는 대로 바로 표시됩니다. 잠시만 기다려 주세요.
        </div>
      )}
    </div>
  );
}

interface Props {
  messages: ChatMessage[];
  customer?: string;
  onConfirm: (botId: string, token: string, accept: boolean) => void;
  onFeedback: (id: string, fb: 1 | -1) => void;
  onEdit?: (text: string) => void;
  onRegenerate?: (question: string) => void;
  onRegenerateSources?: (question: string, excludeUrls: string[], customer?: string) => void;
  onSuggest?: (question: string) => void;
  onBookmark?: (id: string, bookmarkId: number | null) => void;
  // 양식 문서 작성(doc_fill): 해당 bot 답변(query_id)으로 선택한 양식을 채운다
  onDocFill?: (msg: ChatMessage, file: File) => void;
}

export default function MessageList({ messages, customer, onConfirm, onFeedback, onEdit, onRegenerate, onRegenerateSources, onSuggest, onBookmark, onDocFill }: Props) {
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-4">
      {messages.map((m, i) => {
        const prevQuestion =
          i > 0 && messages[i - 1].role === "user" ? messages[i - 1].text : "";
        const split = m.role === "bot" ? splitSources(m.text) : { body: m.text, sources: [] };
        const sources = (m.sources?.length ?? 0) > 0 ? m.sources! : split.sources;
        return m.role === "user" ? (
          <div key={m.id} className="group flex items-center justify-end gap-1">
            {onEdit && (
              <button
                onClick={() => onEdit(m.text)}
                className="shrink-0 rounded-md p-1 text-muted-foreground opacity-0 hover:bg-muted group-hover:opacity-100"
                aria-label="질문 편집"
                title="편집해서 다시 질문"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[#EB4E26] px-3.5 py-2 text-sm text-white">
              {/* 첨부 표시 — 파일명 칩 + 이미지 썸네일 + 텍스트 내용 보기
                  (전송 시점 표시 전용 — 새로고침·이력 복원에는 남지 않음) */}
              {(m.attachments?.length ?? 0) > 0 && (
                <div className="mb-1.5 space-y-1.5">
                  {m.attachments!.some((a) => a.kind === "image" && a.url) && (
                    <div className="flex flex-wrap gap-1.5">
                      {m.attachments!
                        .filter((a) => a.kind === "image" && a.url)
                        .map((a, k) => (
                          <img
                            key={k}
                            src={a.url}
                            alt={a.name}
                            title={`${a.name} — 클릭하면 원본 크기로 열립니다`}
                            onClick={() => window.open(a.url, "_blank")}
                            className="h-24 max-w-[48%] cursor-zoom-in rounded-lg border border-white/30 object-cover"
                          />
                        ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {m.attachments!.map((a, k) => (
                      <span
                        key={k}
                        title={a.name}
                        className="flex max-w-full items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[11px]"
                      >
                        {a.kind === "image" ? (
                          <ImageIcon className="h-3 w-3 shrink-0" />
                        ) : (
                          <FileText className="h-3 w-3 shrink-0" />
                        )}
                        <span className="max-w-[40vw] truncate">{a.name}</span>
                        {a.size > 0 && (
                          <span className="shrink-0 opacity-70">({fmtAttachSize(a.size)})</span>
                        )}
                      </span>
                    ))}
                  </div>
                  {m.attachments!
                    .filter((a) => a.kind === "text" && (a.preview || a.url))
                    .map((a, k) => (
                      <TextAttachmentView key={k} name={a.name} preview={a.preview} url={a.url} />
                    ))}
                </div>
              )}
              <div className="whitespace-pre-wrap">{m.text}</div>
            </div>
          </div>
        ) : (
          <div key={m.id} className="flex justify-start">
            <div className="max-w-[92%] rounded-2xl rounded-bl-sm border border-border bg-card px-3.5 py-2.5 text-sm text-card-foreground">
              {m.blocked ? (
                <div className="flex items-start gap-2 text-muted-foreground">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{m.text || "민감정보가 포함되어 답변을 제공할 수 없습니다."}</span>
                </div>
              ) : m.streaming && !m.text ? (
                <WaitingIndicator startedAt={m.startedAt} progress={m.progress} />
              ) : (
                <div className={cn("md", m.streaming && "cursor-blink", m.error && "text-destructive")}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ href, children }) => {
                        if (href && href.startsWith("#cite-")) {
                          const n = href.slice("#cite-".length);
                          return (
                            <sup>
                              <button
                                type="button"
                                onClick={() => scrollToCite(`isac-src-${m.id}`, n)}
                                className="mx-0.5 rounded bg-[#EB4E26]/10 px-1 py-0.5 align-super text-[10px] font-semibold text-[#EB4E26] hover:bg-[#EB4E26]/20"
                                title={`근거 ${n} 보기`}
                              >
                                {n}
                              </button>
                            </sup>
                          );
                        }
                        return <SafeLink href={href}>{children}</SafeLink>;
                      },
                    }}
                  >
                    {linkifyCitations(split.body, sources.length)}
                  </ReactMarkdown>
                </div>
              )}

              {/* 기능②: 근거 부족(모름) → 담당자 에스컬레이션 안내 */}
              {!m.streaming && m.noAnswer && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{m.escalation || "지식 저장소에서 관련 문서를 찾지 못했습니다."}</span>
                </div>
              )}

              {!m.streaming && !m.blocked && (
                <SourceList
                  sources={sources}
                  question={prevQuestion || undefined}
                  customer={customer}
                  onRegenerate={onRegenerateSources}
                  meta={m.sourceMeta}
                  idPrefix={`isac-src-${m.id}`}
                />
              )}

              {!m.streaming && (m.downloads?.length ?? 0) > 0 && (
                <div className="mt-3 space-y-1.5 border-t border-border pt-2">
                  <div className="text-xs font-semibold text-muted-foreground">다운로드</div>
                  {m.downloads!.map((d, k) => (
                    <a
                      key={k}
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1.5 rounded-md bg-[#EB4E26] px-3 py-1.5 text-xs font-semibold text-white"
                    >
                      <Download className="h-3.5 w-3.5" /> {d.name}
                    </a>
                  ))}
                </div>
              )}

              {m.externalRequired && m.confirmToken && (
                <ConfirmCard
                  onConfirm={() => onConfirm(m.id, m.confirmToken!, true)}
                  onDecline={() => onConfirm(m.id, m.confirmToken!, false)}
                />
              )}

              {!m.streaming && !m.blocked && !m.error && !m.externalRequired && (
                <AnswerActions
                  msg={m}
                  question={prevQuestion}
                  customer={customer}
                  onFeedback={(fb) => onFeedback(m.id, fb)}
                  onRegenerate={
                    onRegenerate && prevQuestion ? () => onRegenerate(prevQuestion) : undefined
                  }
                  onBookmark={onBookmark ? (bid) => onBookmark(m.id, bid) : undefined}
                  // 스킬 산출물(downloads 有) 턴은 '답변'이 아니므로 양식 채움 대상에서 제외
                  onDocFill={onDocFill && !(m.downloads?.length) ? (file) => onDocFill(m, file) : undefined}
                />
              )}

              {!m.streaming && onSuggest && (m.suggestions?.length ?? 0) > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5 border-t border-border pt-2">
                  {m.suggestions!.map((s, k) => (
                    <button
                      key={k}
                      onClick={() => onSuggest(s)}
                      className="rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground hover:bg-muted"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}
